/**
 * ============================================================================
 *  ENVIO DAS INSCRIÇÕES PARA O SUPABASE
 * ============================================================================
 *
 *  Não usamos a biblioteca oficial (@supabase/supabase-js) de propósito: ela
 *  pesa dezenas de KB no bundle e aqui só precisamos de UMA operação — inserir
 *  uma linha. A API do Supabase é HTTP comum, então `fetch` resolve sem
 *  dependência nenhuma.
 *
 *  ----------------------------------------------------------------------
 *  SOBRE A CHAVE PÚBLICA (anon key)
 *  ----------------------------------------------------------------------
 *  Ela fica visível no código que vai para o navegador — e tudo bem, é para
 *  isso que ela existe. Quem protege os dados é a regra de segurança no banco
 *  (RLS, veja supabase/schema.sql): a única coisa que essa chave consegue
 *  fazer é inserir uma inscrição. Ela não lê, não altera e não apaga nada.
 *
 *  A chave `service_role`, essa sim secreta, NUNCA deve aparecer aqui.
 * ============================================================================
 */

import { psConfig } from '../data/processoSeletivoData';

const URL_BASE = process.env.REACT_APP_SUPABASE_URL;
const CHAVE_PUBLICA = process.env.REACT_APP_SUPABASE_ANON_KEY;

/** Sem as variáveis de ambiente, o formulário roda em "modo de teste":
 *  valida tudo e mostra a tela de sucesso, mas não grava em lugar nenhum. */
export const supabaseConfigurado = Boolean(URL_BASE && CHAVE_PUBLICA);

/** Erros que sabemos explicar para quem está preenchendo. */
export class ErroDeInscricao extends Error {
  constructor(mensagem, { campo = null, causa = null } = {}) {
    super(mensagem);
    this.name = 'ErroDeInscricao';
    this.campo = campo; // id da pergunta a destacar, quando fizer sentido
    this.causa = causa;
  }
}

/**
 * Grava uma inscrição. Resolve em silêncio quando dá certo e lança
 * ErroDeInscricao quando dá errado.
 */
export async function enviarInscricao(dados) {
  if (!supabaseConfigurado) {
    console.warn(
      '[PS] Supabase não configurado — a inscrição NÃO foi gravada.\n' +
        'Defina REACT_APP_SUPABASE_URL e REACT_APP_SUPABASE_ANON_KEY. ' +
        'Passo a passo em SUPABASE-SETUP.md.'
    );
    console.log('[PS] Dados que seriam enviados:', dados);
    return;
  }

  let resposta;
  try {
    resposta = await fetch(`${URL_BASE}/rest/v1/${psConfig.tabela}`, {
      method: 'POST',
      headers: {
        apikey: CHAVE_PUBLICA,
        Authorization: `Bearer ${CHAVE_PUBLICA}`,
        'Content-Type': 'application/json',
        // Sem isto o Supabase tenta devolver a linha inserida — e a leitura
        // é justamente o que a regra de segurança bloqueia.
        Prefer: 'return=minimal',
      },
      body: JSON.stringify(dados),
    });
  } catch (erro) {
    // Falha de rede: o navegador nem conseguiu falar com o servidor
    throw new ErroDeInscricao(
      'Não conseguimos falar com o servidor. Verifique sua conexão e tente enviar de novo — suas respostas continuam salvas.',
      { causa: erro }
    );
  }

  if (resposta.ok) return;

  // Deu erro: o Supabase devolve um JSON com o código do problema
  let detalhe = {};
  try {
    detalhe = await resposta.json();
  } catch {
    /* resposta sem corpo legível */
  }

  const codigo = detalhe.code;

  // 23505 = violação de índice único → já existe inscrição com este e-mail
  if (codigo === '23505') {
    throw new ErroDeInscricao(
      'Já existe uma inscrição com este e-mail. Se você acha que houve um engano, fale com a presidência da Liga.',
      { campo: 'email', causa: detalhe }
    );
  }

  // 23514 = violação de restrição (valor fora do permitido ou texto longo demais)
  if (codigo === '23514') {
    throw new ErroDeInscricao(
      'Alguma resposta não foi aceita pelo servidor. Revise os campos e tente novamente.',
      { causa: detalhe }
    );
  }

  if (resposta.status === 401 || resposta.status === 403) {
    console.error('[PS] Supabase recusou a gravação. Confira as chaves e a política de RLS.', detalhe);
    throw new ErroDeInscricao(
      'O sistema de inscrições está temporariamente indisponível. Avise a presidência da Liga, por favor.',
      { causa: detalhe }
    );
  }

  console.error('[PS] Erro inesperado ao gravar a inscrição:', resposta.status, detalhe);
  throw new ErroDeInscricao(
    'Não conseguimos registrar sua inscrição agora. Tente de novo em alguns instantes — suas respostas continuam salvas.',
    { causa: detalhe }
  );
}
